import { useState, useEffect } from 'react'
import { Brain, Plus, Search, Filter, Trash2, Edit2, Eye, Database } from 'lucide-react'
import { memoryApi, MemoryScope, MemoryFact, MemoryCategory } from '../lib/api'
import ScopeModal from '../components/memory/ScopeModal'
import FactModal from '../components/memory/FactModal'
import { useAppLanguage } from '../i18n'

export default function MemoryPage() {
  const { t, language } = useAppLanguage()
  const [activeTab, setActiveTab] = useState<'scopes' | 'facts' | 'categories'>('facts')
  const [scopes, setScopes] = useState<MemoryScope[]>([])
  const [facts, setFacts] = useState<MemoryFact[]>([])
  const [categories, setCategories] = useState<MemoryCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedScope, setSelectedScope] = useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  
  // Modals
  const [showScopeModal, setShowScopeModal] = useState(false)
  const [showFactModal, setShowFactModal] = useState(false)
  const [editingScope, setEditingScope] = useState<MemoryScope | null>(null)
  const [editingFact, setEditingFact] = useState<MemoryFact | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load scopes and facts
      const [scopesRes, factsRes] = await Promise.all([
        memoryApi.listScopes(),
        memoryApi.listFacts(),
      ])
      setScopes(scopesRes?.items || [])
      setFacts(factsRes?.items || [])
      
      // Try to load categories (endpoint may not exist yet)
      try {
        const categoriesRes = await memoryApi.listCategories()
        setCategories(categoriesRes?.items || [])
      } catch (catError) {
        console.warn('Categories endpoint not available:', catError)
        setCategories([])
      }
    } catch (error) {
      console.error(t.memory.errors.loadData, error)
      setScopes([])
      setFacts([])
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const loadFacts = async () => {
    try {
      const params: any = {}
      if (selectedScope) params.scope_id = selectedScope
      if (selectedCategory) params.category = selectedCategory
      const res = await memoryApi.listFacts(params)
      setFacts(res?.items || [])
    } catch (error) {
      console.error(t.memory.errors.loadFacts, error)
      setFacts([])
    }
  }

  useEffect(() => {
    if (activeTab === 'facts') {
      loadFacts()
    }
  }, [selectedScope, selectedCategory])

  const handleDeleteScope = async (id: number) => {
    if (!confirm(t.memory.confirm.deleteScope)) return
    try {
      await memoryApi.deleteScope(id)
      await loadData()
    } catch (error) {
      console.error('Failed to delete scope:', error)
      alert(t.memory.errors.delete)
    }
  }

  const handleDeleteFact = async (id: number) => {
    if (!confirm(t.memory.confirm.deleteFact)) return
    try {
      await memoryApi.deleteFact(id)
      await loadFacts()
    } catch (error) {
      console.error('Failed to delete fact:', error)
      alert(t.memory.errors.delete)
    }
  }

  const filteredFacts = (facts || []).filter(fact => {
    if (searchQuery) {
      return fact.fact_text.toLowerCase().includes(searchQuery.toLowerCase())
    }
    return true
  })

  const getCategoryColor = (slug: string) => {
    const cat = (categories || []).find(c => c.slug === slug)
    return cat?.color || '#3B82F6'
  }

  const getCategoryIcon = (slug: string) => {
    const cat = (categories || []).find(c => c.slug === slug)
    return cat?.icon || 'circle'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1600px] py-3 sm:py-6">

        {/* Tabs */}
        <div className="flex gap-1.5 mb-4 p-1 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30">
          <button
            onClick={() => setActiveTab('facts')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'facts'
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
            }`}
          >
            <Database className="w-4 h-4 shrink-0" />
            <span className="truncate">{t.memory.tabs.facts} ({facts.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('scopes')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'scopes'
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
            }`}
          >
            <Filter className="w-4 h-4 shrink-0" />
            <span className="truncate">{t.memory.tabs.scopes} ({scopes.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'categories'
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
            }`}
          >
            <span className="truncate">{t.memory.tabs.categories} ({categories.length})</span>
          </button>
        </div>

        {/* Facts Tab */}
        {activeTab === 'facts' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t.memory.filters.searchPlaceholder}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={selectedScope || ''}
                  onChange={(e) => setSelectedScope(e.target.value ? Number(e.target.value) : null)}
                  className="flex-1 min-w-0 px-3 py-2 text-sm rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">{t.memory.filters.allScopes}</option>
                  {scopes.map(scope => (
                    <option key={scope.id} value={scope.id}>{scope.name}</option>
                  ))}
                </select>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 text-sm rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">{t.memory.filters.allCategories}</option>
                  {categories.map(cat => (
                    <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => { setEditingFact(null); setShowFactModal(true) }}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t.memory.buttons.newFact}</span>
                </button>
              </div>
            </div>

            {/* Facts List */}
            <div className="grid grid-cols-1 gap-4">
              {filteredFacts.length === 0 ? (
                <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-10 text-center">
                  <Database className="w-12 h-12 mx-auto mb-3 text-zinc-400" />
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">{t.memory.empty.facts}</p>
                </div>
              ) : (
                filteredFacts.map(fact => (
                  <div
                    key={fact.id}
                    className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-3 sm:p-4 hover:shadow-lg transition-all"
                  >
                    {/* Ligne 1 : badges + actions */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0 flex-wrap">
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium text-white shrink-0"
                          style={{ backgroundColor: getCategoryColor(fact.category) }}
                        >
                          {fact.category}
                        </span>
                        {fact.confidence_level === 'verified' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 shrink-0">
                            ✓ {t.memory.badges.verified}
                          </span>
                        )}
                        {fact.is_redacted && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                            {t.memory.badges.redacted}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => { setEditingFact(fact); setShowFactModal(true) }}
                          className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                          title={t.memory.tooltips.edit}
                        >
                          <Edit2 className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteFact(fact.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title={t.memory.tooltips.delete}
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </div>
                    {/* Ligne 2 : texte */}
                    <p className="mt-2 text-sm text-zinc-900 dark:text-zinc-100 font-medium">
                      {fact.fact_text}
                    </p>
                    {fact.fact_summary && (
                      <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">{fact.fact_summary}</p>
                    )}
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-zinc-500 dark:text-zinc-500">
                      <span>{t.memory.labels.scope}: {scopes.find(s => s.id === fact.memory_scope_id)?.name || 'N/A'}</span>
                      <span>·</span>
                      <span>{t.memory.labels.source}: {fact.source_type}</span>
                      {fact.usage_count > 0 && (
                        <><span>·</span><span>{t.memory.labels.used} {fact.usage_count}{t.memory.labels.times}</span></>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Scopes Tab */}
        {activeTab === 'scopes' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <button
                onClick={() => { setEditingScope(null); setShowScopeModal(true) }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl transition-all"
              >
                <Plus className="w-4 h-4" />
                {t.memory.buttons.newScope}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {scopes.map(scope => (
                <div
                  key={scope.id}
                  className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-4 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{scope.name}</h3>
                      {scope.description && (
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5 line-clamp-2">{scope.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => { setEditingScope(scope); setShowScopeModal(true) }}
                        className="p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
                      </button>
                      <button
                        onClick={() => handleDeleteScope(scope.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className={`px-2 py-0.5 rounded-full ${
                      scope.scope_type === 'tenant'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                    }`}>
                      {scope.scope_type === 'tenant' ? t.memory.badges.business : t.memory.badges.endUser}
                    </span>
                    <span>·</span>
                    <span>{facts.filter(f => f.memory_scope_id === scope.id).length} {t.memory.labels.facts}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map(cat => (
              <div
                key={cat.id}
                className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-emerald-200/50 dark:border-emerald-800/30 p-4"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-white font-semibold text-sm"
                    style={{ backgroundColor: cat.color }}
                  >
                    {cat.icon || cat.name[0]}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{cat.name}</h3>
                    {cat.is_system && (
                      <span className="text-xs text-zinc-500">{t.memory.badges.system}</span>
                    )}
                  </div>
                </div>
                {cat.description && (
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-2">{cat.description}</p>
                )}
                <div className="text-xs text-zinc-500">
                  {cat.default_ttl_days ? `${t.memory.labels.ttl}: ${cat.default_ttl_days} ${t.memory.labels.days}` : t.memory.labels.permanent}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showScopeModal && (
        <ScopeModal
          scope={editingScope}
          onClose={() => {
            setShowScopeModal(false)
            setEditingScope(null)
          }}
          onSave={loadData}
        />
      )}

      {showFactModal && (
        <FactModal
          fact={editingFact}
          scopes={scopes}
          categories={categories}
          onClose={() => {
            setShowFactModal(false)
            setEditingFact(null)
          }}
          onSave={loadFacts}
        />
      )}
    </div>
  )
}
